import React from 'react'
import SurveyListCard from './components/SurveyListCard'
import { SurveyCompletionState } from './components/SurveyListCard/styles'
import { type StudentDistribution } from '../../models/Distribution'
import PageHeader from '../../components/PageHeader'
import { isStudentSurveyReopenEnabled } from '../../utils/tenantFeatureFlags'

interface SurveyListFeatureProps {
  list: StudentDistribution[]
  studentName: string
  /**
   * Paging context for the card's "survey N of M" aria-label. Without it a screen reader on page 2
   * announces the 21st survey as the first. Optional so an unpaginated caller still renders right.
   */
  pageNumber?: number
  pageSize?: number
  totalResults?: number
}

// CE-193: when reopen is enabled (Pitt only), a submitted survey stays actionable
// as "Reopen Survey" until the end date; afterwards it falls back to the shared
// disabled "Submitted" state. All non-reopen tenants keep the original behavior.
export const getSurveyState = (isSubmitted: boolean, endTimestamp: string, reopenEnabled = false): SurveyCompletionState => {
  const currentUtcDate = new Date().toISOString()
  if (isSubmitted) {
    if (reopenEnabled && currentUtcDate < endTimestamp) {
      return SurveyCompletionState.ReopenSurvey
    }
    return SurveyCompletionState.Submitted
  } else if (currentUtcDate >= endTimestamp) {
    return SurveyCompletionState.Closed
  } else {
    return SurveyCompletionState.StartSurvey
  }
}

const SurveyListFeature: React.FC<SurveyListFeatureProps> = ({
  list,
  studentName,
  pageNumber = 1,
  pageSize,
  totalResults
}): JSX.Element => {
  const reopenEnabled = isStudentSurveyReopenEnabled()
  const cardsPerPage = pageSize ?? list?.length ?? 0
  const indexOffset = (pageNumber - 1) * cardsPerPage
  const announcedTotal = totalResults ?? list?.length
  return (
    <React.Fragment>
      <PageHeader
        title={studentName}
        pageTitleAriaLabel={`Page title ${studentName}`}
        isCompactOnly
      />
      {list.map((item, index) => (
        <SurveyListCard
          index={indexOffset + index}
          total={announcedTotal}
          // Keyed on the row's own id, not its array position. The list is ordered by the
          // caller's own response status first, so submitting a survey re-buckets its row and
          // reorders the list — with an index key React would match cards to positions rather
          // than to surveys across that reorder.
          key={item.distributionId}
          title={item.courseDetails.courseName}
          owner={item.courseDetails.instructorName}
          // Change survey State to enum for 'draft survey' feature
          surveyState={getSurveyState(item.isSubmitted, item.endTimestamp, reopenEnabled)}
          endDate={item.endTimestamp}
          surveyURL={item.distributionUrl ?? undefined}
          surveyId={item.distributionId}
          courseId={item?.courseDetails?.courseId}
          isSandbox={item?.isSandbox}
        />
      ))}
    </React.Fragment>
  )
}

export default SurveyListFeature
